// Configurações → Anti-spam: envios do formulário público que o submit-lead
// pôs em quarentena (honeypot preenchido ou enviado rápido demais). Nada aqui
// entra no funil nem gera e-mail; o operador restaura o que for gente real.
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  useDeleteLeadRejection,
  useLeadRejections,
  useRestoreLeadRejection,
  type LeadRejection,
} from "@/hooks/useLeadRejections";
import { rejectionReasonLabel } from "@/lib/leadIntake";

export function LeadRejectionsTab() {
  const { data: rejections, isLoading } = useLeadRejections();

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
      <div className="flex flex-col gap-1 border-b border-border/70 px-6 pb-3.5 pt-[18px]">
        <h2 className="m-0 text-[15px] font-bold">Envios bloqueados</h2>
        <p className="m-0 text-[12.5px] text-muted-foreground">
          Passaram no captcha, mas preencheram o campo-armadilha ou enviaram o formulário rápido
          demais. Não viram lead nem recebem e-mail. Guardados por 90 dias.
        </p>
      </div>
      {isLoading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Carregando...</div>
      ) : !rejections || rejections.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Nenhum envio bloqueado.
        </div>
      ) : (
        rejections.map((r) => <RejectionRow key={r.id} rejection={r} />)
      )}
    </section>
  );
}

function RejectionRow({ rejection: r }: { rejection: LeadRejection }) {
  const navigate = useNavigate();
  const restore = useRestoreLeadRejection();
  const remove = useDeleteLeadRejection();
  const busy = restore.isPending || remove.isPending;

  const handleRestore = async () => {
    try {
      const leadId = await restore.mutateAsync(r.id);
      toast.success("Lead restaurado");
      navigate(`/admin/leads/${leadId}`);
    } catch {
      toast.error("Não foi possível restaurar o envio");
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(r.id);
      toast.success("Envio excluído");
    } catch {
      toast.error("Não foi possível excluir o envio");
    }
  };

  return (
    <div className="flex flex-col gap-3 border-b border-border/70 px-6 py-4 last:border-b-0 sm:flex-row sm:items-start">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[13.5px] font-semibold">{r.name}</span>
          <StatusBadge tone="warn" shape="tag">
            {rejectionReasonLabel(r.reason)}
          </StatusBadge>
        </div>
        <div className="truncate text-[11.5px] text-muted-foreground">
          {r.email}
          {r.phone ? ` · ${r.phone}` : ""}
          {r.source ? ` · ${r.source}` : ""}
        </div>
        <p className="m-0 mt-1.5 line-clamp-2 whitespace-pre-line text-[12.5px] text-foreground/80">
          {r.message}
        </p>
        <div className="mt-1 text-[11px] text-muted-foreground">
          {format(new Date(r.created_at), "dd/MM/yyyy HH:mm")}
          {r.elapsed_ms !== null ? ` · preenchido em ${(r.elapsed_ms / 1000).toFixed(1)} s` : ""}
          {r.honeypot_value ? ` · armadilha: "${r.honeypot_value}"` : ""}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="outline"
          className="h-8 rounded-lg px-3 text-xs font-semibold"
          onClick={handleRestore}
          disabled={busy}
        >
          {restore.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="h-3.5 w-3.5" />
          )}
          Restaurar como lead
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-bad"
              aria-label="Excluir envio bloqueado"
              disabled={busy}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir envio bloqueado?</AlertDialogTitle>
              <AlertDialogDescription>
                O envio de {r.name} será apagado de vez. Não dá para restaurar depois.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-bad text-bad-foreground hover:bg-bad/90"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
